import type { CategoryCounts, PeerReportSummary } from './peerReportSummary';

export type Grade3 = 'EX' | 'GD' | 'NI';

export type PeerBehaviorSuggestion = {
  grade: Grade3;
  positivityRate: number;
  goodTotal: number;
  badTotal: number;
  participantCount: number;
  byCategoryRates: Record<string, { good: number; bad: number; rate: number | null }>;
  reason: string;
};

function catTotals(byCategory?: CategoryCounts | null) {
  const keys = ['Ownership', 'Co-work', 'Professionalism'] as const;
  let good = 0;
  let bad = 0;
  const byCategoryRates: PeerBehaviorSuggestion['byCategoryRates'] = {};
  keys.forEach((k) => {
    const g = byCategory?.[k]?.good || 0;
    const b = byCategory?.[k]?.bad || 0;
    const answered = g + b;
    byCategoryRates[k] = {
      good: g,
      bad: b,
      rate: answered > 0 ? Math.round((g / answered) * 100) : null,
    };
    good += g;
    bad += b;
  });
  return { good, bad, byCategoryRates };
}

/**
 * 다면평가(일하는 방식) 익명 집계 → Calibration 행동(일하는 방식) 등급 추천
 * - Good 비중 80%↑ → EX
 * - 50~80% → GD
 * - 50%↓ → NI
 * 응답(Good/Bad)이 없으면 추천 없음
 */
export function suggestBehaviorFromPeer(
  summary: Partial<PeerReportSummary> | null | undefined
): PeerBehaviorSuggestion | null {
  if (!summary) return null;
  const participantCount = Number(summary.participantCount || 0);
  const { good, bad, byCategoryRates } = catTotals(summary.byCategory as CategoryCounts | undefined);
  const answered = good + bad;
  if (answered === 0) return null;

  const positivityRate = Math.round((good / answered) * 100);
  let grade: Grade3 = 'GD';
  if (positivityRate >= 80) grade = 'EX';
  else if (positivityRate < 50) grade = 'NI';

  const reason =
    participantCount > 0
      ? `다면평가 ${participantCount}명 참여 · Good ${good} / Bad ${bad} (긍정 ${positivityRate}%)`
      : `다면평가 집계 · Good ${good} / Bad ${bad} (긍정 ${positivityRate}%)`;

  return {
    grade,
    positivityRate,
    goodTotal: good,
    badTotal: bad,
    participantCount,
    byCategoryRates,
    reason,
  };
}

export function gradeLabelKo(grade: Grade3 | string): string {
  if (grade === 'EX') return '훌륭해요 (EX)';
  if (grade === 'GD') return '준수해요 (GD)';
  if (grade === 'NI') return '노력해요 (NI)';
  return String(grade);
}
